import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { emailChangeActions, emailChangeApiActions } from './email-change.actions';
import { EmailChangeInfraestructure } from './email-change.infraestructure';

@Injectable()
export class EmailChangeEffects {
  readonly #actions$ = inject(Actions);
  readonly #emailChangeInfraestructure = inject(EmailChangeInfraestructure);
  readonly #storeErrorHandler = inject(StoreErrorHandler);

  public startProcess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(emailChangeActions.startProcess),
      switchMap((data) =>
        this.#emailChangeInfraestructure.startProcess(data.newEmail).pipe(
          map((response) => emailChangeApiActions.startProcessSuccess({ requestId: response.emailChangeRequestId })),
          catchError((error: HttpErrorResponse) => of(emailChangeApiActions.startProcessFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(emailChangeApiActions.startProcessFailure({ error }))),
    );
  });

  public sendCodeOldEmail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(emailChangeActions.sendCodeOldEmail),
      switchMap((data) =>
        this.#emailChangeInfraestructure.sendCodeOldEmail(data.requestId).pipe(
          map(() => emailChangeApiActions.sendCodeOldEmailSuccess()),
          catchError((error: HttpErrorResponse) => of(emailChangeApiActions.sendCodeOldEmailFailure({ error }))),
        ),
      ),
    );
  });

  public verifyOldEmail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(emailChangeActions.verifyOldEmail),
      switchMap((data) =>
        this.#emailChangeInfraestructure.verifyOldEmail(data.code, data.requestId).pipe(
          map((response) => emailChangeApiActions.verifyOldEmailSuccess({ interested: response.valid })),
          catchError((error: HttpErrorResponse) => of(emailChangeApiActions.verifyOldEmailFailure({ error }))),
        ),
      ),
    );
  });

  public sendCodeNewEmail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(emailChangeActions.sendCodeNewEmail),
      switchMap((data) =>
        this.#emailChangeInfraestructure.sendCodeNewEmail(data.requestId).pipe(
          map(() => emailChangeApiActions.sendCodeNewEmailSuccess()),
          catchError((error: HttpErrorResponse) => of(emailChangeApiActions.sendCodeNewEmailFailure({ error }))),
        ),
      ),
    );
  });

  public verifyNewEmail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(emailChangeActions.verifyNewEmail),
      switchMap((data) =>
        this.#emailChangeInfraestructure.verifyNewEmail(data.code, data.requestId).pipe(
          map((response) => emailChangeApiActions.verifyNewEmailSuccess({ interested: response.valid })),
          catchError((error: HttpErrorResponse) => of(emailChangeApiActions.verifyNewEmailFailure({ error }))),
        ),
      ),
    );
  });

  public abortProcess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(emailChangeActions.abortProcess),
      switchMap((data) =>
        this.#emailChangeInfraestructure.abortProcess(data.requestId).pipe(
          map(() => emailChangeApiActions.abortProcessSuccess()),
          catchError((error: HttpErrorResponse) => of(emailChangeApiActions.abortProcessFailure({ error }))),
        ),
      ),
    );
  });

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(emailChangeApiActions.startProcessFailure),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
